import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';

import {
  ModalHeader,
  VerticalFlexContainer,
} from './ModalContainers/ModalContainers';

import HandOffLink from '../../../utils/urls/HandOffLink';

import colors from '../../../styling/colors';
import {
  MOBILE_END_QUERY,
  DESKTOP_START_QUERY,
} from '../../../styling/mixins/responsive';
import {
  BasisFont,
  fontSizeStandard,
  paragraph,
  trackedStyles,
} from '../../../styling/mixins/typography';

import {
  EMAIL_BUNDLE_SIZE,
  getEmailSendPrices,
  getPremiumFlyerPrices,
} from '../../../utils/products';

const BASE_DESIGN_PRICE = 'Free';

const FullHeightVerticalFlexContainer = styled(VerticalFlexContainer)`
  ${BasisFont}
  height: 100%;
`;

const ContentArea = styled.div`
  color: ${colors('lapel')};
  background-color: ${colors('dove')};
  padding: 0 0.5rem 2rem;
  flex-direction: column;

  @media (${DESKTOP_START_QUERY}) {
    max-width: 100%;
    flex-direction: row;
    padding: 0 1rem 2rem;
  }
`;

const ContentGrid = styled.div`
  display: flex;
  justify-content: center;

  @media (${MOBILE_END_QUERY}) {
    flex-direction: column;
  }

  @media (${DESKTOP_START_QUERY}) {
    .priceBlock:nth-child(odd) {
      margin-right: 1rem;
    }
  }
`;

const ContentColumn = styled.div`
  background-color: ${colors('white')};
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1.5rem 1.5rem 0;

  &.bottom-spacer {
    padding-bottom: 1.5rem;
  }

  @media (${DESKTOP_START_QUERY}) {
    padding: 1.5rem;
    width: 45%;
  }
`;

const MobileCollapser = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  div:last-child {
    border-bottom: 0;
  }

  @media (${MOBILE_END_QUERY}) {
    margin-bottom: 0;
  }
`;

const FootContent = styled.div`
  margin: 2rem auto;
  text-align: center;
`;

const NeedMoreInvitations = styled.div`
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 0.75rem;
  font-weight: 700;
`;

const BuyBundles = styled.div`
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.43;
  color: ${colors('boulder')};

  @media (${DESKTOP_START_QUERY}) {
    font-weight: 500;
  }
`;

const HorizontalRule = styled.hr`
  margin: 1.5rem 0;
  border: 0.5px solid ${colors('silver')};
`;

const ContactQuestions = styled.div`
  font-size: 0.875rem;
  font-weight: 300;
  line-height: 1.4;
  color: ${colors('boulder')};
`;

const SupportLink = styled(HandOffLink)`
  color: ${colors('boulder')};
  padding-bottom: 2px;
  border-bottom: 1px solid ${colors('boulder')};
`;

const ModalHeaderNoBorder = styled(ModalHeader)`
  ${trackedStyles}
  font-size: 0.75rem;
  margin: 0;
  line-height: 0.75;
  background-color: ${colors('dove')};
  border-bottom: none;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 1.5rem 0 0;
`;

const ComparisonTitle = styled.h2`
  font-size: 1.25rem;
  line-height: 1.6;
  font-weight: 500;
  text-align: center;
  padding: 1.5rem 0 1rem;
`;

const ComparisonHeader = styled.div`
  width: 100%;
  ${paragraph}
  margin-bottom: 0;
  font-weight: 500;

  @media (${DESKTOP_START_QUERY}) {
    font-size: 1rem;
  }
`;

const Line = styled.div`
  width: 100%;
  padding: 1rem 0;
  border-bottom: solid 1px rgba(151, 151, 151, 0.2);
`;

const Left = styled.div`
  color: ${colors('boulder')};
  float: left;
`;

const Right = styled.div`
  font-size: 1rem;
  font-weight: 500;
  float: right;
`;

const OldPriceText = styled.span`
  margin-right: 0.5rem;
  opacity: 0.5;
  text-decoration: line-through;
`;

const DiscountPromotion = styled.span`
  ${fontSizeStandard}
  color: #ff8669;
  float: right;
`;

const Title = styled.div`
  width: 16rem;
  margin: 0 auto 4rem;
  font-size: 1.25rem;
  text-align: center;
  font-weight: 500;
  line-height: 1.2;

  @media (${DESKTOP_START_QUERY}) {
    width: 100%;
    color: gray;
    margin: 0;
    font-weight: 600;
    letter-spacing: 2px;
    font-size: 0.625rem;
  }
`;

/**
 * FlyerPricingModal
 *
 * @property {function} onClose - function to close the modal
 * @property {string} url - URL to the iframe to display
 */
export class FlyerPricingModal extends Component {
  // Process the product pricing data in a way that is useful for rendering purposes.
  // Both Standard and Premium products can independently keep track of their own
  // prices and discounts so that these do not have to be similar in the future.
  getPricing = () => {
    const { products } = this.props;

    const {
      currentPrice: currentEmailPrice,
      discount: emailPriceDiscount,
      hasDiscount: hasEmailDiscount,
      originalPrice: originalEmailPrice,
    } = getEmailSendPrices(products);
    const {
      currentPrice: currentPremiumPrice,
      discount: premiumPriceDiscount,
      hasDiscount: hasPremiumDiscount,
      originalPrice: originalPremiumPrice,
    } = getPremiumFlyerPrices(products);

    return {
      currentEmailPrice,
      premiumDesignPricing: {
        discountEmail: hasEmailDiscount ? `${emailPriceDiscount}% off!` : null,
        emailPricing: [null, null, null, null].map((_, i) => ({
          currentPrice: currentPremiumPrice + currentEmailPrice * i,
          emailCount: EMAIL_BUNDLE_SIZE + EMAIL_BUNDLE_SIZE * i,
          originalPrice: originalPremiumPrice + originalEmailPrice * i,
        })),
        hasDiscount: hasPremiumDiscount,
        linkPricing: {
          currentPrice: currentPremiumPrice,
          discount: hasPremiumDiscount ? `${premiumPriceDiscount}% off!` : null,
          originalPrice: originalPremiumPrice,
        },
      },
      standardDesignPricing: {
        discountEmail: hasEmailDiscount ? `${emailPriceDiscount}% off!` : null,
        emailPricing: [null, null, null, null].map((_, i) => ({
          currentPrice: currentEmailPrice * i || BASE_DESIGN_PRICE,
          emailCount: EMAIL_BUNDLE_SIZE + EMAIL_BUNDLE_SIZE * i,
          originalPrice: originalEmailPrice * i,
        })),
        hasDiscount: hasEmailDiscount,
        linkPricing: {
          currentPrice: BASE_DESIGN_PRICE,
          discount: null,
          originalPrice: null,
        },
      },
    };
  };

  render() {
    const { isMobile } = this.props;

    const {
      currentEmailPrice,
      premiumDesignPricing: {
        discountEmail: premiumEmailDiscount,
        emailPricing: premiumEmailPricing,
        hasDiscount: hasPremiumDiscount,
        linkPricing: premiumLinkPricing,
      },
      standardDesignPricing: {
        discountEmail: standardEmailDiscount,
        emailPricing: standardEmailPricing,
        hasDiscount: hasEmailDiscount,
        linkPricing: standardLinkPricing,
      },
    } = this.getPricing();

    return (
      <FullHeightVerticalFlexContainer>
        <ModalHeaderNoBorder isMobile={isMobile}>
          Flyer Pricing
        </ModalHeaderNoBorder>
        <ContentArea>
          <ComparisonTitle>Share or text a link</ComparisonTitle>
          <ContentGrid>
            <ContentColumn className="priceBlock">
              <MobileCollapser>
                <ComparisonHeader>Standard designs</ComparisonHeader>
                <Line>
                  <Left>Share or text to unlimited guests</Left>
                  <Right>{standardLinkPricing.currentPrice}</Right>
                </Line>
              </MobileCollapser>
            </ContentColumn>
            <ContentColumn className="bottom-spacer">
              <MobileCollapser>
                <ComparisonHeader>
                  Premium designs
                  {hasPremiumDiscount && (
                    <DiscountPromotion>
                      {premiumLinkPricing.discount}
                    </DiscountPromotion>
                  )}
                </ComparisonHeader>
                <Line>
                  <Left>Share or text to unlimited guests</Left>
                  <Right>
                    {hasPremiumDiscount && (
                      <OldPriceText>
                        ${premiumLinkPricing.originalPrice}
                      </OldPriceText>
                    )}
                    ${premiumLinkPricing.currentPrice}
                  </Right>
                </Line>
              </MobileCollapser>
            </ContentColumn>
          </ContentGrid>

          <ComparisonTitle>Send email invitations</ComparisonTitle>
          <ContentGrid>
            <ContentColumn className="priceBlock">
              <MobileCollapser>
                <ComparisonHeader>
                  Standard designs
                  {hasEmailDiscount && (
                    <DiscountPromotion>
                      {standardEmailDiscount}
                    </DiscountPromotion>
                  )}
                </ComparisonHeader>
                {standardEmailPricing.map(price => {
                  const { currentPrice, emailCount, originalPrice } = price;
                  const isBase = currentPrice === BASE_DESIGN_PRICE;
                  return (
                    <Line>
                      <Left>Up to {emailCount}</Left>
                      <Right>
                        {hasEmailDiscount && !isBase && (
                          <OldPriceText>${originalPrice}</OldPriceText>
                        )}
                        {isBase ? `` : `$`}
                        {currentPrice}
                      </Right>
                    </Line>
                  );
                })}
              </MobileCollapser>
            </ContentColumn>
            <ContentColumn className="bottom-spacer">
              <MobileCollapser>
                <ComparisonHeader>
                  Premium designs
                  {hasEmailDiscount && (
                    <DiscountPromotion>
                      {premiumEmailDiscount}
                    </DiscountPromotion>
                  )}
                </ComparisonHeader>
                {premiumEmailPricing.map((price, i) => {
                  const { currentPrice, emailCount, originalPrice } = price;
                  return (
                    <Line>
                      <Left>Up to {emailCount}</Left>
                      <Right>
                        {hasEmailDiscount && (
                          <OldPriceText>${originalPrice}</OldPriceText>
                        )}
                        ${currentPrice}
                      </Right>
                    </Line>
                  );
                })}
              </MobileCollapser>
            </ContentColumn>
          </ContentGrid>

          <FootContent>
            <NeedMoreInvitations>
              Need more email invitations?
            </NeedMoreInvitations>
            <BuyBundles>
              Buy bundles of {EMAIL_BUNDLE_SIZE} email invites for only $
              {currentEmailPrice}.
              <br />
              There is no maximum sending limit.
            </BuyBundles>
            <ContactQuestions>
              Questions about pricing?{' '}
              <SupportLink identifier="flyer-pricing-modal" href="/contact">
                Get in touch
              </SupportLink>
            </ContactQuestions>
          </FootContent>
        </ContentArea>
      </FullHeightVerticalFlexContainer>
    );
  }
}

const mapStateToProps = ({ env: { isMobile }, modals: { products } }) => ({
  isMobile,
  products,
});

export default connect(mapStateToProps)(FlyerPricingModal);
